/**
 * VoiceCloneCard — Settings surface for creating, viewing, and deleting the
 * user's ElevenLabs voice clone. Only shows when the user is on a paid tier;
 * for free/trial users it renders an upgrade nudge.
 *
 * MVP flow: record 30–60s sample in-browser → upload → clone → done.
 * (File-upload fallback available for users on non-mic browsers.)
 */
import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  Mic,
  Square,
  Trash2,
  Upload,
  Loader2,
  Sparkles,
  Info,
  Play,
  Pause,
} from "lucide-react";
import { Link } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

async function api(path, opts = {}) {
  const r = await fetch(`${API}${path}`, {
    credentials: "include",
    headers: opts.body instanceof FormData ? {} : { "Content-Type": "application/json", ...(opts.headers || {}) },
    ...opts,
  });
  if (!r.ok) {
    const text = await r.text();
    let msg = text;
    try {
      msg = JSON.parse(text).detail || text;
    } catch {}
    const err = new Error(msg);
    err.status = r.status;
    throw err;
  }
  return r.json();
}

const MIN_SAMPLE_SEC = 30;
const MAX_SAMPLE_SEC = 90;

export default function VoiceCloneCard({ onChange } = {}) {
  const [state, setState] = useState(null); // { has_clone, eligible, ... }
  const [loading, setLoading] = useState(true);
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [name, setName] = useState("My voice");
  const [mode, setMode] = useState("ivc");
  const [submitting, setSubmitting] = useState(false);
  const [previewAudio, setPreviewAudio] = useState(null);
  const [playingPreview, setPlayingPreview] = useState(false);

  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  const refresh = async () => {
    try {
      const s = await api("/voice/clone");
      setState(s);
      onChange?.(s);
    } catch (e) {
      setState({ has_clone: false, eligible: false });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    return () => {
      if (audioUrl) URL.revokeObjectURL(audioUrl);
      if (timerRef.current) clearInterval(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      mediaRecorderRef.current = mr;
      chunksRef.current = [];
      mr.ondataavailable = (e) => e.data && e.data.size && chunksRef.current.push(e.data);
      mr.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        setAudioBlob(blob);
        setAudioUrl(URL.createObjectURL(blob));
        stream.getTracks().forEach((t) => t.stop());
      };
      mr.start();
      setRecording(true);
      setElapsed(0);
      timerRef.current = setInterval(() => {
        setElapsed((e) => {
          const next = e + 1;
          if (next >= MAX_SAMPLE_SEC) stopRecording();
          return next;
        });
      }, 1000);
    } catch (e) {
      toast.error("Microphone permission denied.");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current?.state === "recording") {
      mediaRecorderRef.current.stop();
    }
    if (timerRef.current) clearInterval(timerRef.current);
    setRecording(false);
  };

  const resetSample = () => {
    if (audioUrl) URL.revokeObjectURL(audioUrl);
    setAudioBlob(null);
    setAudioUrl(null);
    setElapsed(0);
  };

  const submitClone = async () => {
    if (!audioBlob) {
      toast.error("Record or upload a sample first.");
      return;
    }
    if (elapsed && elapsed < MIN_SAMPLE_SEC) {
      toast.error(`Sample must be at least ${MIN_SAMPLE_SEC}s. Recorded: ${elapsed}s`);
      return;
    }
    try {
      setSubmitting(true);
      const fd = new FormData();
      fd.append("file", audioBlob, "sample.webm");
      fd.append("name", name.trim() || "My voice");
      fd.append("mode", mode);
      await api("/voice/clone", { method: "POST", body: fd });
      toast.success("Voice cloned. Try 'Play in my voice' on any journal entry.");
      resetSample();
      await refresh();
    } catch (e) {
      if (e.status === 402) {
        toast.error("Voice cloning requires a paid tier.");
      } else if (e.status === 409) {
        toast.error("You already have a clone. Delete it first.");
      } else {
        toast.error(`Clone failed: ${e.message?.slice(0, 120)}`);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const deleteClone = async () => {
    if (!window.confirm("Delete your voice clone? All cached narrations will be purged.")) return;
    try {
      await api("/voice/clone", { method: "DELETE" });
      toast.success("Voice clone deleted.");
      await refresh();
    } catch (e) {
      toast.error("Delete failed.");
    }
  };

  const onFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) {
      toast.error("File too large (max 10MB).");
      return;
    }
    if (audioUrl) URL.revokeObjectURL(audioUrl);
    setAudioBlob(f);
    setAudioUrl(URL.createObjectURL(f));
    setElapsed(MIN_SAMPLE_SEC); // trust the file's duration
  };

  const togglePreview = () => {
    if (!audioUrl) return;
    if (!previewAudio) {
      const a = new Audio(audioUrl);
      a.onended = () => setPlayingPreview(false);
      setPreviewAudio(a);
      a.play();
      setPlayingPreview(true);
    } else if (playingPreview) {
      previewAudio.pause();
      setPlayingPreview(false);
    } else {
      previewAudio.play();
      setPlayingPreview(true);
    }
  };

  if (loading) {
    return (
      <div className="border border-obsidian-600 bg-obsidian-800/40 p-6" data-testid="voice-clone-card-loading">
        <Loader2 className="h-4 w-4 animate-spin text-terracotta" />
      </div>
    );
  }

  // Free/trial users: upgrade nudge
  if (!state?.eligible && !state?.has_clone) {
    return (
      <section className="border border-obsidian-600 bg-obsidian-800/40 p-6 sm:p-8" data-testid="voice-clone-locked">
        <div className="flex items-start gap-4 mb-4">
          <div className="h-10 w-10 border border-terracotta/60 flex items-center justify-center flex-shrink-0">
            <Sparkles className="h-4 w-4 text-terracotta" />
          </div>
          <div>
            <h3 className="font-serif text-xl text-bone-100 mb-1">Narrate in your own voice</h3>
            <p className="font-sans text-sm text-bone-400 leading-relaxed">
              Clone your speaking voice once — then hear every journal entry read back in it.
              Available on Quarterly, Yearly, and Lifetime tiers.
            </p>
          </div>
        </div>
        <Link
          to="/pricing"
          data-testid="voice-clone-upgrade-link"
          className="inline-flex items-center gap-2 border border-terracotta bg-terracotta hover:bg-terracotta-deep text-bone-100 font-sans uppercase tracking-wider text-xs px-5 py-3"
        >
          See tiers
        </Link>
      </section>
    );
  }

  // Has an existing clone — manage view
  if (state?.has_clone) {
    const pct = Math.min(
      100,
      Math.round((state.monthly_chars_used / state.monthly_char_cap) * 100)
    );
    return (
      <section className="border border-obsidian-600 bg-obsidian-800/40 p-6 sm:p-8" data-testid="voice-clone-manage">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <p className="font-sans uppercase tracking-[0.24em] text-[0.65rem] text-terracotta mb-2">
              Your voice · {state.mode?.toUpperCase() || "IVC"}
            </p>
            <h3 className="font-serif text-xl text-bone-100 mb-1" data-testid="voice-clone-name">
              {state.name}
            </h3>
            <p className="font-sans text-xs text-bone-500">
              Created {new Date(state.created_at).toLocaleDateString()}
            </p>
          </div>
          <Button
            onClick={deleteClone}
            data-testid="voice-clone-delete-btn"
            variant="ghost"
            className="text-bone-500 hover:text-terracotta hover:bg-transparent"
            title="Delete clone"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>

        {/* Usage bar */}
        <div className="mb-2 flex items-center justify-between text-xs font-sans">
          <span className="text-bone-400 uppercase tracking-[0.18em]">Monthly usage</span>
          <span className="text-bone-300" data-testid="voice-clone-usage-text">
            {state.monthly_chars_used?.toLocaleString() || 0} /{" "}
            {state.monthly_char_cap?.toLocaleString()} chars
          </span>
        </div>
        <div className="h-1.5 w-full bg-obsidian-900 border border-obsidian-600">
          <div
            className={`h-full ${pct >= 90 ? "bg-red-500" : pct >= 70 ? "bg-yellow-500" : "bg-terracotta"}`}
            style={{ width: `${pct}%` }}
            data-testid="voice-clone-usage-bar"
          />
        </div>
        <p className="mt-4 font-sans text-xs text-bone-500 flex items-start gap-2">
          <Info className="h-3 w-3 mt-0.5 flex-shrink-0" />
          <span>
            Resets on the 1st. Use "Play in my voice" on any journal entry to hear it in your voice.
          </span>
        </p>
      </section>
    );
  }

  // Eligible + no clone yet — creation flow
  return (
    <section className="border border-obsidian-600 bg-obsidian-800/40 p-6 sm:p-8" data-testid="voice-clone-create">
      <p className="font-sans uppercase tracking-[0.24em] text-[0.65rem] text-terracotta mb-2">
        Voice cloning · Creator
      </p>
      <h3 className="font-serif text-xl text-bone-100 mb-2">Clone your voice</h3>
      <p className="font-sans text-sm text-bone-400 leading-relaxed mb-6">
        Record 30–60 seconds of your natural speaking voice. Read anything —
        a paragraph from a favorite book works well. The Mirror will read
        every future entry back in this voice.
      </p>

      {/* Record or upload */}
      {!audioBlob ? (
        <div className="space-y-3">
          <Button
            onClick={recording ? stopRecording : startRecording}
            data-testid="voice-clone-record-btn"
            className={`w-full rounded-none py-6 font-sans uppercase tracking-wider text-xs ${
              recording
                ? "bg-red-600 hover:bg-red-700 text-bone-100"
                : "bg-terracotta hover:bg-terracotta-deep text-bone-100"
            }`}
          >
            {recording ? (
              <>
                <Square className="h-4 w-4 mr-2" />
                Stop · {elapsed}s
              </>
            ) : (
              <>
                <Mic className="h-4 w-4 mr-2" />
                Record sample
              </>
            )}
          </Button>
          <label
            htmlFor="voice-sample-file"
            className="flex items-center justify-center gap-2 w-full rounded-none border border-obsidian-600 bg-transparent py-5 font-sans uppercase tracking-wider text-xs text-bone-300 hover:border-terracotta hover:text-terracotta cursor-pointer"
            data-testid="voice-clone-upload-label"
          >
            <Upload className="h-4 w-4" />
            or upload audio file (mp3, m4a, wav)
          </label>
          <input
            id="voice-sample-file"
            type="file"
            accept="audio/*"
            onChange={onFileChange}
            className="hidden"
            data-testid="voice-clone-upload-input"
          />
        </div>
      ) : (
        <div className="space-y-4">
          {/* Preview */}
          <div className="flex items-center gap-3 border border-obsidian-600 bg-obsidian-900 p-3">
            <Button
              onClick={togglePreview}
              data-testid="voice-clone-preview-btn"
              variant="ghost"
              className="text-terracotta hover:text-terracotta-deep hover:bg-transparent p-0 h-8 w-8"
            >
              {playingPreview ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5" />}
            </Button>
            <span className="font-sans text-sm text-bone-300 flex-1">
              Sample preview · {elapsed || "?"}s
            </span>
            <Button
              onClick={resetSample}
              data-testid="voice-clone-reset-btn"
              variant="ghost"
              className="text-bone-500 hover:text-terracotta hover:bg-transparent p-0 h-8 w-8"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>

          {/* Name */}
          <div>
            <label htmlFor="clone-name" className="block font-sans uppercase tracking-[0.2em] text-[0.65rem] text-bone-400 mb-2">
              Give your voice a name
            </label>
            <Input
              id="clone-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={40}
              placeholder="My voice"
              data-testid="voice-clone-name-input"
              className="bg-obsidian-900 border-obsidian-600 rounded-none font-sans text-bone-100"
            />
          </div>

          {/* Submit */}
          <Button
            onClick={submitClone}
            disabled={submitting}
            data-testid="voice-clone-submit-btn"
            className="w-full rounded-none bg-terracotta hover:bg-terracotta-deep text-bone-100 font-sans uppercase tracking-wider text-xs py-6"
          >
            {submitting && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            {submitting ? "Cloning your voice…" : "Create voice clone"}
          </Button>
          <p className="font-sans text-[0.65rem] uppercase tracking-[0.24em] text-bone-500 text-center">
            Takes ~20 seconds · your audio sample stays private
          </p>
        </div>
      )}
    </section>
  );
}
